import { Grid, TextField, Divider } from '@atgfr8/atg-ui-tailwind';
import { Button, Rating } from '@components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import {
  RatePayloadType,
  orderStore,
  rateCarrier,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { printLog } from '@utils/utils';
import { isEmpty, size } from 'lodash';
import { createSignal } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import cls from './classes';
import { formSchema } from './validation';

type FormDataType = {
  rating: number;
  comment: string;
};

export const RatingsForm = (props: {
  onClose: () => void;
  modalId: string;
}) => {
  const [isLoading, setIsLoading] = createSignal<boolean>(false);

  const { form, data, setFields, errors } = createForm<FormDataType>({
    initialValues: {
      rating: 3,
      comment: '',
    },
    extend: validator({ schema: formSchema }),
    onSubmit: (values) => {
      void handleOnSubmit(values);
    },
    onError: (errors) => {
      printLog(errors);
    },
  });

  const handleOnSubmit = async (values: FormDataType) => {
    try {
      setIsLoading(true);
      const payload: RatePayloadType = {
        carrierId: orderStore.order.loads[orderStore.activeTab.index]
          .carrierId as number,
        loadId: orderStore.order.loads[orderStore.activeTab.index].id,
        rating: values.rating,
        comment: values.comment,
      };
      const response = await rateCarrier(
        payload,
        CHANGE_TRACKING_SOURCES.orderDetails.loadDetailsCarrierRating,
      );
      if (!isEmpty(response) && Boolean(response.isSuccess)) {
        updateLoadPropertyAtIndex({
          canRate: false,
        });
      }
      props.onClose();
    } catch (error) {
      printLog(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form ref={form}>
      <Grid container spacing={4} class={cls.dialogContentContainer}>
        <Grid item size={12}>
          <div class="flex justify-center text-2xl">
            How Did This Carrier Do?
          </div>
          <Divider class={cls.divider} />
        </Grid>
        <Grid item size={12} class={cls.ratingContainer}>
          <Rating
            currentRating={data().rating}
            totalRatings={5}
            readOnly={false}
            onChange={(newRating) => setFields('rating', newRating)}
            class="text-[#FF9800] mr-[10px] text-xl"
          />
        </Grid>
        <Grid item size={12}>
          <TextField
            dataTestId="comment-textInput"
            id="comment"
            multiline={true}
            rows={5}
            value={data().comment}
            label={'Comment'}
            placeholder={''}
            class="w-full"
            onChange={(value: string) => {
              setFields('comment', value);
            }}
            error={
              size(errors().comment) > 0 ? (errors().comment ?? [])[0] : ''
            }
          />
        </Grid>
      </Grid>
      <Grid class="flex mt-4 justify-end gap-5">
        <Button
          label={'Submit'}
          variant="contained"
          type="submit"
          disabled={isLoading()}
          isLoading={isLoading()}
          dataTestId={formatDataTestId('Submit', 'button')}
        />
        <Button
          label={'Cancel'}
          variant={'text'}
          onClick={() => props.onClose()}
          disabled={isLoading()}
          dataTestId={formatDataTestId('Cancel', 'button')}
        />
      </Grid>
    </form>
  );
};
